import { useMutation, useQuery } from "@apollo/client";
import cn from "classnames";
import { setCookie } from "cookies-next";
import Image from "next/image";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import * as yup from "yup";

import { BASKET_CONTENT } from "@api/mutations/tickets";
import { UPDATE_BUYER_INFO } from "@api/mutationsV2/tickets";
import {
  ADD_VOUCHERS_TO_BASKET,
  CREATE_BASKET,
  VOUCHERS_FOR_SALE,
} from "@api/queries/gift-card";
import BlockTitle from "@components/cms/BlockTitle/BlockTitle";
import { PAYMENT_PRODUCT_TYPES } from "@config/config";
import { useStore } from "@hooks/useZustandProvider";
import campaignsVouchersSvg from "@images/svg/campaigns-vouchers.svg";
import logoGreySvg from "@images/svg/logo-grey.svg";
import Button from "@ui-kit/Button/Button";
import Editor from "@ui-kit/cms/Editor/Editor";
import Container from "@ui-kit/Container/Container";
import Divider from "@ui-kit/Divider/Divider";
import Loading from "@ui-kit/Loading/Loading";
import Select from "@ui-kit/Select/Select";
import SelectOption from "@ui-kit/Select/SelectOption";
import Textarea from "@ui-kit/Textarea/Textarea";
import TicketsInput from "@ui-kit/TicketsInput/TicketsInput";
import { regExpForEmail } from "@utils/utils";

import s from "./GiftCardBlock.module.scss";

const GiftCardBlock = ({ settings, data, updateField }) => {
  const { t } = useTranslation();
  const { locale, ...router } = useRouter();
  const { title, description, termsAndConditions } = data;
  const { blockType } = settings.common;
  const {
    cms,
    app,
    updatePayment,
    updateBasketValid,
    updateGiftCard,
    updateGiftCardFormData,
  } = useStore();
  const { isEditMode } = cms.uiOptions;
  const { currency, currencyName, symbol } = app;
  const [form, setForm] = useState({
    priceId: "",
    email: "",
    receiver: "",
    greeting: "",
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({
    priceId: true,
    email: true,
    receiver: true,
    greeting: true,
  });
  const [vouchers, setVouchers] = useState({
    Prices: [],
  });

  const schema = yup.object().shape({
    priceId: yup.string().required("Value is required"),
    email: yup
      .string()
      .required("Email is required")
      .matches(
        regExpForEmail,
        t("validation.error.email", {
          field: t("email"),
        }),
      )
      .email("Email is invalid"),
    receiver: yup.string().max(
      50,
      t("validation.error.max", {
        field: "receiver",
        max: 50,
      }),
    ),
    greeting: yup.string().max(
      300,
      t("validation.error.max", {
        field: "greeting",
        max: 300,
      }),
    ),
  });

  useQuery(VOUCHERS_FOR_SALE, {
    variables: {
      lang: locale,
    },
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (!data.vouchersForSale?.length) {
        return toast.error("[GET VOUCHERS]: no vouchers");
      }

      const allVouchers = data.vouchersForSale;

      const giftCardVouchers = allVouchers.find((voucher) => {
        return (
          voucher.VoucherTypeId === "VOUCHER_TYPE.GIFT_CARD" &&
          voucher.ServiceId ===
            +process.env.NEXT_PUBLIC_GIFT_CARD_VOUCHERS_SERVICE_ID
        );
      });

      if (!giftCardVouchers) {
        return toast.error(
          "[GET VOUCHERS]: no vouchers: VOUCHER_TYPE.GIFT_CARD",
        );
      }

      setVouchers(giftCardVouchers);
    },
    onError: (error) => {
      toast.error(`[${error.name}]: ${error.message}`);
    },
  });

  const [basketContent, { loading: basketLoading }] = useMutation(
    BASKET_CONTENT,
    {
      onCompleted: (data) => {
        const voucherCode =
          data.basketContent.basketContent?.Services?.[0]?.VoucherCode;
        const totalPrice = data.basketContent.basketContent?.TotalPrice || null;

        if (voucherCode) {
          updateGiftCardFormData(form);
          updateGiftCard({ voucherCode });
          updatePayment({
            totalPrice,
            product: PAYMENT_PRODUCT_TYPES.giftCard,
          });
        } else {
          return toast.error(`[no_voucher_code]: No voucher code.`);
        }

        router.push("/payment");
      },
      onError: (error) => {
        toast.error(`[${error.name}]: ${error.message}`);
      },
    },
  );

  const [addVoucher, { loading: addVoucherLoading }] = useMutation(
    ADD_VOUCHERS_TO_BASKET,
    {
      variables: {
        input: {
          Vouchers: [
            {
              Name: form.receiver,
              GreetingText: form.greeting,
              PriceId: +form.priceId,
              ServiceGuid: vouchers.ServiceGuid,
              VatRate: vouchers.VatRate,
              Quantity: 1,
              CurrencyId: currency,
            },
          ],
        },
      },
      onCompleted: (data) => {
        const error = data.error;

        if (error) {
          return toast.error(`[${error.ErrorCode}]: ${error.ErrorReason}`);
        }

        basketContent();
      },
      onError: (error) => {
        toast.error(`[${error.name}]: ${error.message}`);
      },
    },
  );

  const [updateBuyerInfo, { loading: updateBuyerInfoLoading }] = useMutation(
    UPDATE_BUYER_INFO,
    {
      context: { clientName: "v2" },
      variables: {
        input: {
          Email: form.email,
        },
      },
      onCompleted: (data) => {
        const error = data.error;

        if (error) {
          return toast.error(`[${error.ErrorCode}]: ${error.ErrorReason}`);
        }

        addVoucher();
      },
      onError: (error) => {
        toast.error(`[${error.name}]: ${error.message}`);
      },
    },
  );

  const [createBasket, { loading: basketContentLoading }] = useMutation(
    CREATE_BASKET,
    {
      variables: {
        lang: locale,
        currency,
      },
      onCompleted: (data) => {
        const error = data.error;

        if (error) {
          return toast.error(`[${error.ErrorCode}]: ${error.ErrorReason}`);
        }

        const basket = data.createBasket.basket;
        setCookie("shopping-basket-id", basket.ShoppingBasketId);

        updateBasketValid(basket.ValidUntil);

        updateBuyerInfo();
      },
      onError: (error) => {
        toast.error(`[${error.name}]: ${error.message}`);
      },
    },
  );

  const handleChange = (event) => {
    const { name, value } = event.target;

    // Валидируем только текущее поле
    schema
      .validateAt(name, { [name]: value })
      .then(() => {
        setErrors((prevErrors) => ({ ...prevErrors, [name]: undefined }));
      })
      .catch((err) => {
        setErrors((prevErrors) => ({
          ...prevErrors,
          [name]: err.errors[0],
        }));
      });

    setForm((prevForm) => ({
      ...prevForm,
      [name]: value,
    }));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    schema
      .validateAt(name, form)
      .then(() => {
        setErrors((prevErrors) => ({ ...prevErrors, [name]: undefined }));
      })
      .catch((err) => {
        setErrors((prevErrors) => ({
          ...prevErrors,
          [name]: err.errors[0],
        }));
      });

    setTouched((prevTouched) => ({
      ...prevTouched,
      [name]: true,
    }));
  };

  const handleFocus = (e) => {
    const { name } = e.target;
    setTouched((prevTouched) => ({
      ...prevTouched,
      [name]: false,
    }));
  };

  const handleContinue = (event) => {
    event.preventDefault();
    setTouched((prevTouched) => {
      const touchedFields = {};
      Object.keys(prevTouched).forEach((fieldName) => {
        touchedFields[fieldName] = true;
      });
      return touchedFields;
    });

    schema
      .validate(form, { abortEarly: false })
      .then(() => {
        createBasket();
      })
      .catch((err) => {
        const newErrors = {};
        err.inner.forEach((error) => {
          newErrors[error.path] = error.message;
        });
        setErrors(newErrors);
      });
  };

  useEffect(() => {
    const vouchersByCurrency = vouchers.Prices.filter(
      (v) => v.CurrencyId === currency,
    );

    if (vouchersByCurrency.length) {
      handleChange({
        target: {
          name: "priceId",
          value: vouchersByCurrency[0].PriceId,
        },
      });
    } else {
      handleChange({
        target: {},
      });
    }
  }, [vouchers, currency]);

  const loading =
    basketContentLoading ||
    updateBuyerInfoLoading ||
    addVoucherLoading ||
    basketLoading;

  return (
    <Container className={s.giftCardBlock}>
      <Container full={false}>
        <section className={s.content}>
          <BlockTitle
            settings={settings}
            field={{ title }}
            onChange={updateField}
            type={blockType}
          />
          <p className={s.description}>
            <Editor field={{ description }} onChange={updateField} />
          </p>
          <form onSubmit={handleContinue} className={s.form}>
            <Divider absolute vertical />
            <div className={s.header}>
              <div className={s.headerTitle}>
                <Image
                  src={campaignsVouchersSvg}
                  alt="luxexpress, site logo"
                  className={s.image}
                  height={18}
                />
                <div className={s.text}>{t("page.giftCard.form.title")}</div>
              </div>
              <div className={s.logo}>
                <Image
                  src={logoGreySvg}
                  alt="luxexpress, site logo"
                  width={72}
                  height={18}
                />
              </div>
            </div>
            <div className={s.row}>
              <div className={cn(s.field, s.select)}>
                <label>{t("page.giftCard.form.field.value.label")} *</label>
                <Select
                  name="priceId"
                  value={form.priceId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onFocus={handleFocus}
                  withoutMessage
                  required
                  errorWithoutMessage={touched.priceId && errors.priceId}
                >
                  {vouchers.Prices.filter((v) => v.CurrencyId === currency).map(
                    (voucher) => {
                      return (
                        <SelectOption
                          key={voucher.PriceId}
                          value={voucher.PriceId}
                          text={`${voucher.Price} ${symbol}`}
                        />
                      );
                    },
                  )}
                </Select>
              </div>

              <div className={s.field}>
                <TicketsInput
                  required
                  type="text"
                  name="email"
                  label={t("page.giftCard.form.field.email.label")}
                  value={form.email}
                  onBlur={handleBlur}
                  onFocus={handleFocus}
                  onChange={handleChange}
                  errors={touched.email && errors.email}
                  placeholder={t("page.giftCard.form.field.email.placeholder")}
                />
              </div>
            </div>
            <div className={s.row}>
              <div className={s.field}>
                <TicketsInput
                  type="text"
                  name="receiver"
                  label={t("page.giftCard.form.field.receiver.label")}
                  placeholder={t(
                    "page.giftCard.form.field.receiver.placehlder",
                  )}
                  value={form.receiver}
                  onBlur={handleBlur}
                  onFocus={handleFocus}
                  onChange={handleChange}
                  errors={touched.receiver && errors.receiver}
                />
              </div>
            </div>
            <div className={s.row}>
              <div className={s.field}>
                <Textarea
                  name="greeting"
                  label={t("page.giftCard.form.field.greeting.label")}
                  placeholder={t(
                    "page.giftCard.form.field.greeting.placehlder",
                  )}
                  value={form.greeting}
                  onBlur={handleBlur}
                  onFocus={handleFocus}
                  onChange={handleChange}
                  errors={touched.greeting && errors.greeting}
                />
              </div>
            </div>
            {!isEditMode && (
              <div className={s.row}>
                <Button type="submit" disabled={loading} className={s.continue}>
                  {loading ? (
                    <Loading size={8} />
                  ) : (
                    t("page.giftCard.form.button")
                  )}
                </Button>
              </div>
            )}
          </form>
          <p className={s.terms}>
            <Editor field={{ termsAndConditions }} onChange={updateField} />
          </p>
        </section>
      </Container>
    </Container>
  );
};

export default GiftCardBlock;
